import { Link } from 'react-router-dom';
import { Building2, ExternalLink, UserRound } from 'lucide-react';
import type { LeadPlatformPerformance, SalesSubject } from '@/client/types/sales';
import { formatGhs } from '@/client/types/sales';
import { leadPerformanceFor } from '../lead-performance';
import { Panel, compactGhs } from './sales-ui';
import { cn } from '@/lib/utils';

const ROLE_BADGE: Record<LeadPlatformPerformance['orgs'][number]['role'], string> = {
  owner: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  admin: 'bg-sky-50 text-sky-700 border-sky-200',
  member: 'bg-gray-50 text-gray-600 border-gray-200',
};

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-gray-400">{hint}</p> : null}
    </div>
  );
}

export function LeadPerformancePanel({ subject }: { subject: SalesSubject }) {
  const perf = leadPerformanceFor(subject);
  const snap = subject.platform;

  if (!snap?.uid && !snap?.oid) {
    return (
      <Panel title="Platform performance">
        <p className="text-sm text-gray-500">
          Not on FeelyFeely yet. Win a <span className="font-medium text-gray-700">signup</span> goal to bind a user and
          keep tracking listings and spend across their organizations — the journey does not end at signup.
        </p>
      </Panel>
    );
  }

  if (!perf) {
    // Org-only subject (oid, no uid) — thin snapshot until membership graph exists.
    return (
      <Panel title="Platform performance">
        <dl className="space-y-1 text-sm text-gray-700">
          {snap.oid != null && (
            <div>
              Org{' '}
              <Link className="text-blue-700 hover:underline" to={`/admin/organizations/${snap.oid}`}>
                {snap.oid}
              </Link>
            </div>
          )}
          <div>Listings {snap.listingCount}</div>
          <div>Lifetime paid {formatGhs(snap.lifetimePaidGhs)}</div>
        </dl>
        <p className="mt-3 text-xs text-gray-400">Bind a uid to roll up performance across every org they belong to.</p>
      </Panel>
    );
  }

  const { totals, orgs, personal, uid } = perf;

  return (
    <Panel
      title="Platform performance"
      actions={
        <Link
          to={`/admin/users/${uid}?tab=organizations`}
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline"
        >
          Open user
          <ExternalLink className="h-3 w-3" />
        </Link>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-600">
        <UserRound className="h-4 w-4 text-gray-400" />
        <span>
          Linked user{' '}
          <Link className="font-medium text-blue-700 hover:underline" to={`/admin/users/${uid}`}>
            {uid}
          </Link>
        </span>
        {perf.linkedAt ? (
          <span className="text-xs text-gray-400">· since {new Date(perf.linkedAt).toLocaleDateString()}</span>
        ) : null}
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
          Mock rollup
        </span>
      </div>

      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Orgs" value={String(totals.orgCount)} hint="Memberships for this uid" />
        <Kpi label="Listings" value={String(totals.listingCount)} hint="Personal + all orgs" />
        <Kpi label="Lifetime paid" value={compactGhs(totals.lifetimePaidGhs)} hint="Payments by uid + oid" />
        <Kpi label="Promo spend" value={compactGhs(totals.promoSpendGhs)} hint="Promoted listings" />
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 text-left text-[11px] font-medium uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2 text-right">Listings</th>
              <th className="px-3 py-2 text-right">Active</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Promo</th>
              <th className="px-3 py-2 text-right">Last active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            <tr className="bg-slate-50/60">
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <UserRound className="h-3.5 w-3.5 text-gray-400" />
                  <div>
                    <Link className="font-medium text-gray-900 hover:text-blue-700" to={`/admin/users/${uid}?tab=listings`}>
                      Personal account
                    </Link>
                    <p className="text-[11px] text-gray-400">uid {uid}</p>
                  </div>
                </div>
              </td>
              <td className="px-3 py-2.5 text-xs text-gray-400">—</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{personal.listingCount}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">—</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatGhs(personal.lifetimePaidGhs)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">—</td>
              <td className="px-3 py-2.5 text-right text-xs text-gray-500">
                {snap.lastActiveAt ? new Date(snap.lastActiveAt).toLocaleDateString() : '—'}
              </td>
            </tr>
            {orgs.map((org) => (
              <tr key={org.oid} className="hover:bg-gray-50/80">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-gray-400" />
                    <div>
                      <Link
                        className="font-medium text-gray-900 hover:text-blue-700"
                        to={`/admin/organizations/${org.oid}?tab=listings`}
                      >
                        {org.name}
                      </Link>
                      <p className="text-[11px] text-gray-400">oid {org.oid}</p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span
                    className={cn(
                      'inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize',
                      ROLE_BADGE[org.role],
                    )}
                  >
                    {org.role}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{org.listingCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{org.activeListings}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatGhs(org.lifetimePaidGhs)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatGhs(org.promoSpendGhs)}</td>
                <td className="px-3 py-2.5 text-right text-xs text-gray-500">
                  {org.lastActiveAt ? new Date(org.lastActiveAt).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
            {orgs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-400">
                  No organizations yet — supply goal often creates the first org.
                </td>
              </tr>
            )}
          </tbody>
          {orgs.length > 0 && (
            <tfoot className="border-t border-gray-200 bg-gray-50 text-sm font-medium text-gray-900">
              <tr>
                <td className="px-3 py-2.5" colSpan={2}>
                  Total across personal + orgs
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{totals.listingCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gray-400">—</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatGhs(totals.lifetimePaidGhs)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatGhs(totals.promoSpendGhs)}</td>
                <td className="px-3 py-2.5" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <p className="mt-3 text-[11px] text-gray-400">
        Sketch: listings from user/org listing APIs; paid from payments by uid/oid; promo from promoted-listing owner
        rollups. Deep links go to live admin surfaces.
      </p>
    </Panel>
  );
}
